// © Microsoft Corporation. All rights reserved.

import { AccentButton, Button, OutlineButton } from "@microsoft/fast-components-react-msft";

import React from "react";
import { SocialLocalizedStrings } from "../Social.localizedStrings";
import { SocialProps } from "../Social.props";
import { SocialService } from "@msnews/social-data-service";
import { classNames } from "@microsoft/fast-web-utilities";
import { close } from "../utils/icons";
import { getExtension } from "../utils/fileHelpers";
import { logger } from "@msnews/diagnostics";

export interface CopyrightFormProps extends SocialProps {
    reportType: string;
    reportItemType?: string;
    onReport?: (key: string, e: any) => void;
    t: SocialLocalizedStrings;
}

const MaxFileSize = 1024 * 1024 * 10;  // bytes
const MaxFileCount = 10;
const AcceptedAttachmentTypes = [".pdf", ".txt", ".docx", ".msg", ".xlsx", ".xls"];

enum FileUploadState {
    waiting = "waiting",
    uploading = "uploading",
    uploaded = "uploaded",
    failed = "failed"
}
function getFileUploadStateString(uploadState: FileUploadState, t: SocialLocalizedStrings) {
    return {
        [FileUploadState.waiting]: t.CopyrightFormFileWaiting || "Waiting",
        [FileUploadState.uploading]: t.CopyrightFormFileUploading || "Uploading",
        [FileUploadState.uploaded]: t.CopyrightFormFileUploaded || "Uploaded",
        [FileUploadState.failed]: t.CopyrightFormFileUploadFailed || "Failed"
    }[uploadState];
}

enum FileUploadFailureReason {
    size = "size",
    format = "format",
    count = "count",
    other = "other"
}
function getFileUploadFailureString(failureReason: FileUploadFailureReason, t: SocialLocalizedStrings) {
    return {
        [FileUploadFailureReason.size]: t.CopyrightFormFileSizeFailure || "File size exceeds 10MB",
        [FileUploadFailureReason.format]: t.CopyrightFormFileFormatFailure || "File format is not supported",
        [FileUploadFailureReason.count]: t.CopyrightFormFileCountFailure || "Maximum 10 files can be uploaded",
        [FileUploadFailureReason.other]: t.CopyrightFormFileOtherFailure || "Something went wrong, try again"
    }[failureReason];
}

interface FileState {
    fileName?: string
    uploadState?: FileUploadState,
    uploadFailureReason?: FileUploadFailureReason,
    fileId?: string,
}

interface CopyrightFormState {
    step: number;
    contactInfo: {
        firstName: string;
        lastName: string;
        email: string
    },
    details: {
        authorizedExample: string;
        description: string
    },
    files: {[key: string]: FileState},
    formIsValid: boolean,
    validationErrors: {
        firstNameError: string;
        lastNameError: string;
        emailError: string;
        emailSyntaxError: string;
        authorizedError: string;
        descriptionError: string;
    }
}
const step2DefaultState = {
    contactInfo: {
        firstName: "",
        lastName: "",
        email: ""
    },
    details: {
        authorizedExample: "",
        description: ""
    },
    files: {},
    formIsValid: false,
    validationErrors: {
        firstNameError: "",
        lastNameError: "",
        emailError: "",
        emailSyntaxError: "",
        authorizedError: "",
        descriptionError: ""
    }
};
export default class CopyrightForm extends React.Component<CopyrightFormProps, CopyrightFormState> {
    fileUploadAction: React.RefObject<HTMLInputElement>;

    constructor(props) {
        super(props);
        this.state= {
            step: 0,
            ...step2DefaultState
        };
        this.fileUploadAction = React.createRef();
    }

    handleValidation() {
        const {contactInfo, details} = this.state;
        const {t} = this.props;

        const defaultInvalidMessage =  t.CopyrightFormRequiredField;

        const getErrorValue = (field) => !field ? defaultInvalidMessage : "";

        const isEmailValid = (email) => {
            return typeof email !== "undefined" && !!email && /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email);
        };

        const calculatedErrors = {
            firstNameError: getErrorValue(contactInfo.firstName),
            lastNameError: getErrorValue(contactInfo.lastName),
            emailError: getErrorValue(contactInfo.email),
            emailSyntaxError: !isEmailValid(contactInfo.email) ? t.CopyrightFormEmailValidation : "",
            authorizedError: getErrorValue(details.authorizedExample),
            descriptionError: getErrorValue(details.description)
        };

        const hasError = Object.values(calculatedErrors).some(err => err != "");

        this.setState({
            validationErrors: calculatedErrors,
            formIsValid: !hasError
        });

        return !hasError;
    }

    updateFileState(fileKey: string, fileName: string, uploadState?: FileUploadState, fileId?: string, uploadFailureReason?: FileUploadFailureReason) {
        this.setState(prevState => {
            const files = prevState.files;
            const fileState: FileState = files[fileKey] || {};
            return {
                files: {
                    ...files,
                    [fileKey]: {
                        fileName: fileName,
                        uploadState: uploadState ? uploadState : fileState.uploadState,
                        fileId: fileId ? fileId : fileState.fileId,
                        uploadFailureReason: uploadFailureReason ? uploadFailureReason : fileState.uploadFailureReason
                    }
                }
            };
        });
    }

    async fileSelectHandler(e) {
        if (e && e.target && e.target.files && e.target.files.length > 0) {
            const eventTarget = e.target;
            const selectedFiles = eventTarget.files;
            const getFileKey = (file: File) => `${file.name}-${file.lastModified}-${file.size}`;

            Object.values(selectedFiles).forEach(
                (file: File) => {
                    const fileKey = getFileKey(file);
                    if (!this.state.files[fileKey]) {
                        this.updateFileState(getFileKey(file), file.name, FileUploadState.waiting);
                    }
                }
            );

            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                const fileKey = getFileKey(file);
                const fileExtension = getExtension(file.name);
                const isFileAlreadyHandled = this.state.files[fileKey] && this.state.files[fileKey].uploadState !== FileUploadState.waiting;
                const uploadedFileCount = Object.values(this.state.files).filter(f => f.uploadState === FileUploadState.uploaded).length;
                const updateFileState = this.updateFileState.bind(this, fileKey, file.name);

                if (!isFileAlreadyHandled) {
                    if (file.size > MaxFileSize) {
                        updateFileState(FileUploadState.failed, undefined, FileUploadFailureReason.size);
                    } else if (!AcceptedAttachmentTypes.includes(`.${fileExtension}`)) {
                        updateFileState(FileUploadState.failed, undefined, FileUploadFailureReason.format);
                    } else if (uploadedFileCount >= MaxFileCount) {
                        updateFileState(FileUploadState.failed, undefined, FileUploadFailureReason.count);
                    } else {
                        try {
                            updateFileState(FileUploadState.uploading);
                            const fileId = await SocialService.uploadCopyrightFile(file);
                            updateFileState(FileUploadState.uploaded, fileId);
                        } catch (error) {
                            logger.logError(`Social experience comment report copyright form file upload failed! The reason: ${error}`);
                            updateFileState(FileUploadState.failed, undefined, FileUploadFailureReason.other);
                        }
                    }
                }
            }
            eventTarget.value = null; // This is necessary to able to select the same file twice. (See: https://stackoverflow.com/questions/12030686)
        }
    }

    submitForm(e) {
        e.preventDefault();
        if (this.handleValidation()) {
            const intellectualPropertyReportParams = {
                reportMessage: this.state.details.description,
                intellectualPropertyDetails: {
                    FirstName: this.state.contactInfo.firstName,
                    LastName: this.state.contactInfo.lastName,
                    EmailAddress: this.state.contactInfo.email,
                    AuthorizedExample: this.state.details.authorizedExample,
                    AttachedFiles: Object.values(this.state.files).filter(file => file.uploadState === FileUploadState.uploaded).map(file => file.fileId)
                }
            };
            if (this.props.reportItemType) {
                SocialService.reportUser(this.props.id, this.props.reportItemType, this.props.reportType, intellectualPropertyReportParams);
            } else {
                SocialService.reportComment(this.props.id, this.props.reportType, intellectualPropertyReportParams);
            }
            this.props.onReport && this.props.onReport(this.props.reportType, e);
            this.props.popupConnector.closePopup();
        }
    }

    handleContactInfoOnChange(e) {
        const fieldName = e.target.name;
        this.setState({
            ...this.state,
            contactInfo: {
                ...this.state.contactInfo,
                [fieldName]: e.target.value
            },
            validationErrors: {
                ...this.state.validationErrors,
                [fieldName + "Error"]: "",
                emailSyntaxError: fieldName === "email" ? "" : this.state.validationErrors.emailSyntaxError
            }
        });
    }

    render() {
        return (this.state.step == 0 && this.step0()) || (this.state.step == 1 && this.step1());
    }

    step0() {
        const {managedClasses, t, reportType, reportItemType} = this.props;
        return (
            <div className={classNames(
                managedClasses.copyrightFormContainer,
                this.props.responsiveClass
            )}>
                <div className="cwt-header-container">
                    <header className="cwt-title text-center">
                        <h3 className="cwt-main-title">{t.CopyrightFormCheckTxt}</h3>
                        <p className="cwt-sub-text">{t.CopyrightFormCheckSubTxt}</p>
                    </header>
                </div>
                <hr className="cwt-h-seperator"/>
                <div className="cwt-input-container">
                    <label className="cwt-header-sections">{t.CopyrightFormContentIssueTitle}</label>
                    <input disabled={true} className ="cwt-input-box cwt-input-disabled" type ="text" name ="reportType" value = {reportType} />
                </div>
                <div className="cwt-input-container">
                    <label className="cwt-header-comment-id">{reportItemType ? t.CopyrightFormUserIDTitle : t.CopyrightFormCommentIDTitle}</label>
                    <input disabled={true} className="cwt-input-box cwt-input-disabled" type ="text" name ="commentId" value={this.props.id} />
                </div>
                {this.renderButtonField(() => this.props.popupConnector.closePopup(), t.DeleteDataCancel, () => this.setState({...this.state, step: 1}), t.CopyrightFormYesContinueButton)}
            </div>
        );
    }

    step1() {
        const {managedClasses, t}=this.props;
        const {firstName, lastName, email}=this.state.contactInfo;
        const {authorizedExample, description}=this.state.details;
        const {firstNameError, lastNameError, emailError, authorizedError, descriptionError} = this.state.validationErrors;
        const fileCount = Object.keys(this.state.files).length;
        const waitingOrUploadingFileCount = Object.values(this.state.files).filter(
            f => f.uploadState === FileUploadState.waiting || f.uploadState === FileUploadState.uploading
        ).length;
        const acceptedAttachmentTypes = AcceptedAttachmentTypes.join(", ");

        return (
            <div className={classNames(
                managedClasses.copyrightFormContainer,
                this.props.responsiveClass
            )}>
                <div className="cwt-header-container">
                    <header className="cwt-title text-center">
                        <h3 className="cwt-main-title">{t.CopyrightFormMainTxt}</h3>
                        <p className="cwt-sub-text">{t.CopyrightFormMainSubTxt}</p>
                    </header>
                </div>
                <hr className="cwt-h-seperator"/>
                <div>
                    <header className="cwt-header-sections">{t.CopyrightFormContactInfoTitle}</header>
                    {this.renderInputField(t.CopyrightFormContactInfoFirstName, "firstName", firstName, firstNameError)}
                    {this.renderInputField(t.CopyrightFormContactInfoLastName, "lastName", lastName, lastNameError)}
                    {this.renderInputField(t.CopyrightFormContactInfoEmail, "email", email, emailError)}
                    { (this.state.validationErrors.emailSyntaxError) && <span className= "cwt-input-label cwt-required-text">{t.CopyrightFormEmailValidation}</span> }
                </div>
                <hr className="cwt-h-seperator"/>
                <div>
                    <header className="cwt-header-sections">{t.CopyrightFormDetailsTitle}</header>
                    <div className="cwt-input-container">
                        <label className = "cwt-input-label">{t.CopyrightFormAuthorizedExample}<span className="cwt-input-label-asterisk">{authorizedError && <span className= "cwt-required-text">{ t.CopyrightFormRequiredField }</span> }</span></label>
                        <p className="cwt-sub-text">{t.CopyrightFormAuthorizedExampleSubTxt}</p>
                        <input className = {(authorizedError != "") ? "cwt-input-box cwt-input-box-error" : "cwt-input-box"} type = "text" name="authorizedExample" value = {authorizedExample} onChange = {(e)=>this.setState({...this.state, details: {...this.state.details, [e.target.name]: e.target.value}, validationErrors: {...this.state.validationErrors, authorizedError: ""}})}></input>
                    </div>
                    <div className="cwt-input-container">
                        <label className="cwt-input-label">{t.CopyrightFormDescription}<span className="cwt-input-label-asterisk">{descriptionError && <span className= "cwt-required-text">{ t.CopyrightFormRequiredField }</span>}</span></label>
                        <p className="cwt-sub-text">{t.CopyrightFormDescriptionSubTxt}</p>
                        <textarea className={classNames(
                            "cwt-input-box",
                            "cwt-input-box-description",
                            ["cwt-input-box-error", !!descriptionError]
                        )}  rows={7} name="description" value={description} onChange={(e)=>this.setState({...this.state, details: {...this.state.details, [e.target.name]: e.target.value}, validationErrors: {...this.state.validationErrors, descriptionError: ""}})}></textarea>
                    </div>
                    <div className="cwt-input-container">
                        <label className="cwt-input-label">{t.CopyrightFormAttachments}</label>
                        <p className="cwt-sub-text">{t.CopyrightFormAttachmentsSubTxt}</p>
                        <div className= "cwt-media-container">
                            <input multiple className="cwt-media-input" accept={acceptedAttachmentTypes} type="file" ref={this.fileUploadAction} onChange={this.fileSelectHandler.bind(this)} onDrop={this.fileSelectHandler.bind(this)} title={t.CopyrightFormUploadFileButton}/>
                            <AccentButton className="cwt-fast-button" onClick= {(e)=>this.fileUploadAction.current.click()}>{t.CopyrightFormUploadFileButton}</AccentButton>
                            {fileCount > 0 ?
                                Object.keys(this.state.files).map(fileKey => {
                                    const fileState = this.state.files[fileKey];
                                    return (
                                        <span key={fileKey} className={classNames(
                                            "cwt-media",
                                            ["cwt-upload-failed", fileState.uploadState === FileUploadState.failed],
                                            ["cwt-upload-succeed", fileState.uploadState === FileUploadState.uploaded]
                                        )}>
                                            <span>
                                                {fileState.fileName} ∙ <span className="cwt-media-status">{getFileUploadStateString(fileState.uploadState, t)} {fileState.uploadState === FileUploadState.failed && ` (${getFileUploadFailureString(fileState.uploadFailureReason, t)})`}</span>
                                            </span>
                                            <Button className="cwt-fast-icon-button" onClick={this.removeFile.bind(this, fileKey)}>
                                                <i className="cwt-icon">{close}</i>
                                            </Button>
                                        </span>
                                    );
                                }) :
                                <span className= "cwt-media-span">{ t.CopyrightFormNoFileChosen }</span>
                            }
                        </div>
                    </div>
                    <p className="cwt-sub-text">{t.CopyrightFormAttachmentsInfoTxt}</p>
                </div>
                <div className="cwt-buttons">
                    <OutlineButton className="cwt-fast-button cwt-fast-button-outline" onClick={()=>this.setState({...this.state, ...step2DefaultState})}>{t.CopyrightFormResetButton}</OutlineButton>
                    <AccentButton disabled={waitingOrUploadingFileCount > 0} className={classNames(
                        "cwt-fast-button"
                    )} onClick={this.submitForm.bind(this)}>{t.CopyrightFormSubmitButton}</AccentButton>
                </div>
            </div>
        );
    }

    renderInputField(label, inputName, inputValue, error) {
        const {t} = this.props;
        return (
            <div className="cwt-input-container">
                <label className="cwt-input-label">{label} <span className="cwt-input-label-asterisk">{error && <span className= "cwt-required-text">{ t.CopyrightFormRequiredField }</span> }</span></label>
                <input className={classNames(
                    "cwt-input-box",
                    ["cwt-input-box-error", !!error]
                )} type="text" name={inputName} value={inputValue} onChange={(e)=>this.handleContactInfoOnChange(e)}></input>
            </div>
        );
    }

    renderButtonField(onClick, btnName, onClick1, btnName1) {
        return (
            <div className="cwt-buttons">
                <OutlineButton className="cwt-fast-button cwt-fast-button-outline" onClick={onClick}>{btnName}</OutlineButton>
                <AccentButton className="cwt-fast-button" onClick={onClick1}>{btnName1}</AccentButton>
            </div>
        );
    }

    removeFile(fileKey: string) {
        const newFiles = {...this.state.files};
        delete newFiles[fileKey];
        this.setState({files: newFiles});
    }
}
